import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { IconArrowDownFull, IconCalendar } from '@/theme/icons';
import { endOfTheWeek, getLocale, startOfTheWeek } from '@/utils/calendar';
import { capitalizeFirstLetter } from '@/utils/index';
import rem from '@/utils/rem';
import { Button, HStack, Icon, Text, useBreakpointValue } from '@chakra-ui/react';
import { format } from 'date-fns';
import { RefObject, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  selectedDate: Date;
  showCalendarIcon?: boolean;
  showDatePicker?: boolean;
  onDatePickerOpen?: () => void;
  borderColor?: string;
  onCategorySpotlight?: boolean;
  isInDayMode?: boolean;
};

const InnerDate = forwardRef(
  (
    {
      selectedDate,
      showCalendarIcon = false,
      showDatePicker = false,
      onDatePickerOpen,
      borderColor = 'stroke-primary',
      onCategorySpotlight = false,
      isInDayMode = false,
    }: Props,
    ref,
  ) => {
    const { i18n } = useTranslation('DayWeekNavigator');
    const locale = getLocale(i18n.language);
    const startDate = selectedDate && capitalizeFirstLetter(format(startOfTheWeek(selectedDate), 'MMM dd', { locale }));
    const endDate = selectedDate && capitalizeFirstLetter(format(endOfTheWeek(selectedDate), 'MMM dd', { locale }));
    const endDateYear = selectedDate && format(endOfTheWeek(selectedDate), 'yyyy', { locale });
    const isDailyPlanPage = useIsDailyPlanPage(isInDayMode);
    const isMobileScreen = useBreakpointValue({ base: true, lg: false });

    const dateLabel = isDailyPlanPage
      ? `${capitalizeFirstLetter(format(selectedDate, 'EEE', { locale }))}, ${capitalizeFirstLetter(format(selectedDate, 'MMM dd, yyyy', { locale }))}`
      : `${startDate} - ${endDate}` + `${!isMobileScreen && !onCategorySpotlight ? `, ${endDateYear}` : ``}`;
    if (showDatePicker) {
      return (
        <Button
          ref={ref as RefObject<HTMLButtonElement>}
          justifyContent="center"
          gap={rem(8)}
          width={onCategorySpotlight ? { lg: rem(180) } : { lg: rem(256) }}
          height="full"
          minHeight={rem(32)}
          borderWidth={rem(1)}
          borderStyle="solid"
          borderColor={borderColor}
          borderRadius={rem(4)}
          backgroundColor="background-primary"
          onClick={() => onDatePickerOpen && onDatePickerOpen()}
          paddingX={rem(8)}
          variant="ghost"
        >
          {showCalendarIcon && <Icon as={IconCalendar} boxSize={rem(14)} />}
          <Text as="span" textStyle={{ base: 'mediumBlack', lg: 'largeBlack' }}>
            {dateLabel}
          </Text>

          <Icon as={IconArrowDownFull} boxSize={rem(7)} />
        </Button>
      );
    }

    return (
      <HStack
        textStyle={{ base: 'mediumBlack', lg: 'largeBlack' }}
        justifyContent="center"
        gap={rem(8)}
        width={{ lg: rem(240) }}
        height="full"
        minHeight={rem(32)}
        borderWidth={rem(1)}
        borderStyle="solid"
        borderColor={borderColor}
        borderRadius={rem(4)}
        backgroundColor="background-primary"
        paddingX={rem(8)}
      >
        {showCalendarIcon && <Icon as={IconCalendar} boxSize={rem(14)} />}
        <Text as="span">{dateLabel}</Text>
      </HStack>
    );
  },
);
export default InnerDate;
